import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { formatMoney, formatMoneyShort } from "../../lib/money";
import { AddButton, Empty, ErrorNote, Pill, type Status, Pager, pageSlice } from "../../components/ui";
import VehicleThumb from "./VehicleThumb";
import { coverPaths } from "./photos";

export interface VehicleRow {
  id: string; stock_no: string; vin: string;
  year: number; make: string; model: string; trim_level: string | null;
  exterior_color: string | null; body_type: string | null;
  odometer: number;
  total_cost_cents: number; asking_price_cents: number;
  status: Status; title_status: string;
  purchase_date: string; status_changed_at: string;
}

const TITLE_LABEL: Record<string, string> = {
  not_received: "Not received", in_hand: "In hand",
  sent_to_state: "Sent to state", with_lienholder: "Lienholder",
};

type SortKey = "stock" | "vehicle" | "miles" | "cost" | "ask" | "gross" | "days" | "status";

const daysSince = (iso: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 864e5));

export default function LotList({ onAdd, onOpen }: {
  onAdd: () => void; onOpen: (id: string) => void;
}) {
  const [rows, setRows] = useState<VehicleRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [make, setMake] = useState("");
  const [status, setStatus] = useState("onlot");
  /** "" every car · "with" only ones that have photos · "without" the gaps. */
  const [photos, setPhotos] = useState("");
  /* Make and model, ascending: the lot reads as a lot, with every BMW
   together. Days on the lot is a report, not a default view. */
  const [sort, setSort] = useState<SortKey>("vehicle");
  // Ascending, so the default make-and-model view runs A to Z.
  const [desc, setDesc] = useState(false);

  /* One query for every cover shot on the page, rather than one per car. */
  const [covers, setCovers] = useState<Record<string, string>>({});
  /* The covers arrive after the rows do. Until they have, every car looks
     like it has no photos, and filtering on that would briefly show the whole
     lot as missing them — so the photo filter waits rather than lying. */
  const [coversReady, setCoversReady] = useState(false);
  useEffect(() => {
    if (!rows?.length) return;
    setCoversReady(false);
    coverPaths(rows.map((v) => v.id))
      .then(setCovers)
      .finally(() => setCoversReady(true));
  }, [rows]);

  const hasPhoto = (v: VehicleRow) => Boolean(covers[v.id]);

  useEffect(() => {
    supabase.from("vehicles")
      .select(`id,stock_no,vin,year,make,model,trim_level,exterior_color,body_type,
               odometer,total_cost_cents,asking_price_cents,status,title_status,
               purchase_date,status_changed_at`)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) setErr("Could not load the lot. Check your connection and try again.");
        else setRows((data ?? []) as VehicleRow[]);
      });
  }, []);

  const makes = useMemo(
    () => Array.from(new Set((rows ?? []).map((v) => v.make))).sort(), [rows]);

  const name = (v: VehicleRow) =>
    `${v.year} ${v.make} ${v.model}${v.trim_level ? ` ${v.trim_level}` : ""}`;
  const gross = (v: VehicleRow) => v.asking_price_cents - v.total_cost_cents;

  const [page, setPage] = useState(0);
  /* Any change to the filters or the ordering starts again at the top:
     staying on page 3 of a result that now has one page shows nothing. */
  useEffect(() => { setPage(0); }, [q, make, status, photos, sort, desc]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const n = q.trim().toLowerCase();
    // A search looks at every vehicle, whatever its status. If you are hunting
    // for a specific car you already know it exists; hiding it because it sold
    // last month is not helpful. Browsing still defaults to what is on the lot.
    const searching = n !== "";
    let out = rows.filter((v) => {
      if (make && v.make !== make) return false;
      /* Applies to a search too, unlike status: looking for the cars you still
         have to photograph is the whole point of this one, and hiding sold
         stock from it is already the status filter's job. */
      if (photos && coversReady) {
        if (photos === "with" && !hasPhoto(v)) return false;
        if (photos === "without" && hasPhoto(v)) return false;
      }
      if (!searching) {
        if (status === "onlot" && v.status === "sold") return false;
        if (status && status !== "onlot" && v.status !== status) return false;
      }
      if (!n) return true;
      return [v.vin, v.stock_no, String(v.year), v.make, v.model,
              v.trim_level ?? "", v.exterior_color ?? "", v.body_type ?? "", name(v)]
        .some((f) => f.toLowerCase().includes(n));
    });

    const val = (v: VehicleRow) => ({
      stock: v.stock_no, vehicle: name(v), miles: v.odometer,
      cost: v.total_cost_cents, ask: v.asking_price_cents, gross: gross(v),
      days: daysSince(v.purchase_date), status: v.status,
    })[sort];

    /* The Vehicle column used to sort on the whole label, "2004 BMW 325Ci",
       which put every 2004 together regardless of make. Sorting a lot by make
       is what you actually want when you are looking down it, so make comes
       first, then model, then year — every BMW together, newest last. */
    const byMake = (a: VehicleRow, b: VehicleRow) =>
      a.make.localeCompare(b.make)
      || a.model.localeCompare(b.model)
      || a.year - b.year;

    return [...out].sort((a, b) => {
      const c = sort === "vehicle" ? byMake(a, b) : (() => {
        const x = val(a), y = val(b);
        return typeof x === "number" && typeof y === "number"
          ? x - y : String(x).localeCompare(String(y));
      })();
      return desc ? -c : c;
    });
  }, [rows, q, make, status, photos, covers, coversReady, sort, desc]);


  /* Only this page is rendered; `shown` stays the full filtered set so the
     counts and the pager describe everything that matched. */
  const paged = pageSlice(shown, page);
  if (err) return <ErrorNote>{err}</ErrorNote>;
  if (!rows) return <p className="hint">Loading vehicles</p>;

  const live = rows.filter((v) => v.status !== "sold");
  const tiedUp = live.reduce((n, v) => n + v.total_cost_cents, 0);
  const noTitle = live.filter((v) => v.title_status === "not_received").length;
  /* Worth stating on the header rather than only behind a filter: a car with
     no photographs is a car the public site will not list at all. */
  const noPhotos = coversReady
    ? live.filter((v) => !hasPhoto(v)).length : 0;

  const th = (key: SortKey, label: string, align = "") => (
    <th className={`${align} ${sort === key ? "sorted" : ""}`}
        onClick={() => { sort === key ? setDesc(!desc) : (setSort(key), setDesc(true)); }}>
      {label}{sort === key && <span className="arrow">{desc ? "▾" : "▴"}</span>}
    </th>
  );

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>Vehicles</h2>
          <p className="pagehead-sub">
            {live.length} on the lot · {formatMoney(tiedUp)} tied up
            {noTitle > 0 ? ` · ${noTitle} without title` : ""}
            {noPhotos > 0 ? ` · ${noPhotos} without photos` : ""}
          </p>
        </div>
        <AddButton label="Add vehicle" onClick={onAdd} />
      </div>

      {rows.length === 0 ? (
        <Empty>Nothing on the lot yet.<br />Add your first car to get started.</Empty>
      ) : (
        <>
          <div className="filters">
            <input className="search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search VIN, stock, year, make, model, color" />
            <select value={make} onChange={(e) => setMake(e.target.value)}>
              <option value="">All makes</option>
              {makes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    disabled={q.trim() !== ""}
                    title={q.trim() ? "Searching covers every status" : undefined}>
              <option value="onlot">On the lot</option>
              <option value="">Everything</option>
              <option value="available">Available</option>
              <option value="in_recon">In recon</option>
              <option value="on_hold">On deposit</option>
              <option value="sold">Sold</option>
            </select>
            {/* Its own control rather than more entries on the status menu:
                photographed and available are different questions, and you
                usually want them together — "available, no photos yet". */}
            <select value={photos} onChange={(e) => setPhotos(e.target.value)}
                    disabled={!coversReady}
                    title={coversReady ? undefined : "Checking which cars have photos"}>
              <option value="">Photos: any</option>
              <option value="with">Has photos</option>
              <option value="without">No photos</option>
            </select>
          </div>

          {(q || make || status !== "onlot" || photos) && (
            <p className="resultnote">
              {shown.length} of {rows.length}
              {q && " — searching every vehicle, sold ones included"} —{" "}
              <button className="linkbtn"
                      onClick={() => {
                        setQ(""); setMake(""); setStatus("onlot"); setPhotos("");
                      }}>
                reset
              </button>
            </p>
          )}

          {shown.length === 0 ? <Empty>Nothing matches that.</Empty> : (
            <>
              <div className="tablewrap">
                <table className="dealtable">
                  <thead>
                    <tr>
                      {th("stock", "Stock")}
                      {th("vehicle", "Make and model")}
                      <th>Color</th>
                      {th("miles", "Miles", "num")}
                      {th("cost", "Cost", "num")}
                      {th("ask", "Asking", "num")}
                      {th("gross", "Gross", "num")}
                      <th>Title</th>
                      {th("days", "Days", "num")}
                      {th("status", "Status")}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((v) => (
                      <tr key={v.id} onClick={() => onOpen(v.id)}
                          className={v.status === "sold" ? "muted" : ""}>
                        <td className="mono dim">{v.stock_no}</td>
                        <td className="strong">
                          <span className="withthumb">
                            <VehicleThumb path={covers[v.id]} alt="" />
                            {name(v)}
                          </span>
                        </td>
                        <td>{v.exterior_color ?? "—"}</td>
                        <td className="num">{v.odometer.toLocaleString("en-US")}</td>
                        <td className="num">{formatMoneyShort(v.total_cost_cents)}</td>
                        {/* A car with no asking price has not been priced
                            yet. Showing $0 and a red loss against its cost
                            reads as money lost rather than a job not done. */}
                        <td className="num strong">
                          {v.asking_price_cents > 0
                            ? formatMoneyShort(v.asking_price_cents)
                            : <span className="dim">not priced</span>}
                        </td>
                        <td className={`num ${v.asking_price_cents > 0
                              ? `gross ${gross(v) < 0 ? "negative" : ""}` : "dim"}`}>
                          {v.asking_price_cents > 0 ? formatMoneyShort(gross(v)) : "—"}
                        </td>
                        <td className={v.title_status === "not_received" ? "warn" : ""}>
                          {TITLE_LABEL[v.title_status] ?? v.title_status}
                        </td>
                        <td className="num">{daysSince(v.purchase_date)}</td>
                        <td><Pill status={v.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rows dealcards">
                {paged.map((v) => (
                  <button key={v.id} className={`row ${v.status}`} onClick={() => onOpen(v.id)}>
                    <div className="row-top">
                      <span className="desc withthumb">
                        <VehicleThumb path={covers[v.id]} alt="" />
                        {name(v)}
                      </span>
                      <span className="price">
                        {v.asking_price_cents > 0
                          ? formatMoneyShort(v.asking_price_cents)
                          : <span className="dim">not priced</span>}
                      </span>
                    </div>
                    <div className="row-bot">
                      <span className="meta">
                        <span className="stock mono">{v.stock_no}</span>
                        <span>{v.odometer.toLocaleString("en-US")} mi</span>
                        {v.title_status === "not_received" && <span className="warn">No title</span>}
                      </span>
                      <Pill status={v.status} />
                    </div>
                  </button>
                ))}
              </div>
              <Pager page={page} setPage={setPage} total={shown.length} />
            </>
          )}
        </>
      )}
    </>
  );
}
